import React, { useState, useEffect } from "react";
import axios from "axios";
import { Loader2, DollarSign, TrendingUp, Wallet, PieChart, Layers, Bell, FileText, Send, BarChart3, Settings } from "lucide-react";
import EntityLink, { Breadcrumbs } from "../components/shared/EntityLink";
import EntityNotes from "../components/shared/EntityNotes";
import EntityComms from "../components/shared/EntityComms";
import ProfileDocuments from "../components/shared/ProfileDocuments";
import { Entity360Shell, MetricCard, TimelineCard, FundingProgressBar, ReputationDetailCard } from "../components/shared/OSComponents";
import { InvestorServicingPanel } from "../components/shared/ServicingPanel";
import { fmtUSD } from "../utils/currency";

import { useAuth } from "../context/AuthContext";
import { usePageNavigate } from "../hooks/usePageNavigate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOAN_LABELS = { fix_flip: "Fix & Flip", bridge: "Bridge", dscr: "DSCR", cash_out: "Cash-Out Refi", construction: "Construction", ground_up: "Ground Up" };
const INTERNAL_ROLES = new Set(["admin", "originations", "underwriter", "processor", "lender_relations", "investor_relations"]);

export default function Investor360Page({ userId, onBack, breadcrumbs = [] }) {
  const { navigateToEntity } = usePageNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("commitments");
  const [reputation, setReputation] = useState(null);
  const { user: authUser } = useAuth();
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };
  const isInternal = authUser && INTERNAL_ROLES.has(authUser.role);

  useEffect(() => {
    axios.get(`${API}/entity/investor/${userId}/360`, { headers }).then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false));
    axios.get(`${API}/reputation/investor/${userId}`, { headers }).then(r => setReputation(r.data)).catch(() => {});
  }, [userId]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>;
  if (!data) return <div className="text-center py-20 text-sm text-[#888]">Investor not found</div>;

  const { profile, stats, wallet, commitments, distributions, pool_subscriptions, alerts, documents, preferences, servicing } = data;
  const crumbs = [...breadcrumbs, { type: "investor", id: userId, label: profile.full_name || "Investor" }];
  const ACCRED_COLORS = { verified: "bg-emerald-100 text-emerald-600 border-emerald-200", pending: "bg-amber-100 text-amber-600 border-amber-200", rejected: "bg-red-100 text-red-600 border-red-200" };

  const relatedEntities = [
    ...commitments.slice(0, 5).map(c => ({ type: "opportunity", id: c.opportunity_id, title: `${LOAN_LABELS[c.loan_type] || "Opportunity"} — ${c.state || ""}`, subtitle: `${fmtUSD(c.amount)} committed` })),
    ...commitments.filter(c => c.lead_id).slice(0, 3).map(c => ({ type: "deal", id: c.lead_id, title: "Related Deal", subtitle: c.property_address || c.state || "" })),
    ...documents.slice(0, 3).map(d => ({ type: "document", id: d.doc_id, title: d.original_filename, subtitle: d.doc_type })),
  ];
  const quickActions = [
    { id: "view_commits", label: "View Commitments", icon: DollarSign, onClick: () => setTab("commitments") },
    { id: "view_dists", label: "View Distributions", icon: TrendingUp, onClick: () => setTab("distributions") },
    ...(isInternal ? [{ id: "manage_prefs", label: "Investment Preferences", icon: Settings, onClick: () => setTab("preferences") }] : []),
    ...(isInternal ? [{ id: "generate_report", label: "Generate Report", icon: BarChart3, onClick: () => {} }] : []),
    ...(isInternal ? [{ id: "add_note", label: "Add Note", icon: Send, onClick: () => setTab("notes") }] : []),
  ];
  const tabs = [
    { key: "commitments", label: `Commitments (${commitments.length})` },
    { key: "distributions", label: `Distributions (${distributions.length})` },
    { key: "servicing", label: "Servicing" },
    ...(isInternal && pool_subscriptions.length ? [{ key: "pools", label: `Pools (${pool_subscriptions.length})` }] : []),
    ...(isInternal && alerts.length ? [{ key: "activity", label: `Activity (${alerts.length})` }] : []),
    { key: "documents", label: `Documents (${documents.length})` },
    ...(preferences ? [{ key: "preferences", label: "Preferences" }] : []),
    { key: "reputation", label: "Reputation" },
    ...(isInternal ? [{ key: "comms", label: "Communications" }] : []),
    ...(isInternal ? [{ key: "notes", label: "Notes" }] : []),
  ];
  const kpis = wallet ? [
    { label: "Available", value: fmtUSD(wallet.available), icon: Wallet, accent: true },
    { label: "Committed", value: fmtUSD(wallet.committed), icon: DollarSign },
    { label: "Total Funded", value: fmtUSD(wallet.total_funded), icon: TrendingUp },
    { label: "Distributed", value: fmtUSD(wallet.total_distributed), icon: PieChart },
    { label: "Investments", value: stats.active_investments, icon: Layers },
  ] : [];
  const badges = [{ label: profile.accreditation_status || "pending", className: ACCRED_COLORS[profile.accreditation_status] || "bg-gray-100 text-gray-600" }];

  return (
    <Entity360Shell type="investor" title={profile.full_name}
      subtitle={[profile.email, profile.investor_type, profile.created_at && `Since ${new Date(profile.created_at).toLocaleDateString()}`].filter(Boolean).join(" | ")}
      badges={badges} kpis={kpis} tabs={tabs} activeTab={tab} onTabChange={setTab} onBack={onBack} breadcrumbs={crumbs} onNavigate={navigateToEntity}
      quickActions={quickActions} reputation={reputation?.score} relatedEntities={relatedEntities}>

      {tab === "commitments" && (
        <div className="space-y-3">
          {commitments.length === 0 ? <div className="rounded-xl border border-[#e8e0d0] bg-white p-8 text-center text-sm text-[#888]">No commitments.</div> : commitments.map(c => (
            <div key={c.commitment_id} className="rounded-xl border border-[#e8e0d0] bg-white p-4 hover:shadow-sm transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <EntityLink type="opportunity" id={c.opportunity_id} label={`${LOAN_LABELS[c.loan_type] || c.loan_type || "Opportunity"} — ${c.state || ""}`} onClick={navigateToEntity} />
                <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${c.status === "pending" ? "bg-amber-100 text-amber-600" : c.status === "approved" ? "bg-emerald-100 text-emerald-600" : "bg-gray-100 text-gray-600"}`}>{c.status}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="text-center p-2 rounded-lg bg-[#faf7f2]"><div className="text-sm font-bold text-[#222]">{fmtUSD(c.amount)}</div><div className="text-[9px] text-[#999]">Committed</div></div>
                <div className="text-center p-2 rounded-lg bg-[#faf7f2]"><div className="text-sm font-bold text-[#222]">{c.target_raise ? fmtUSD(c.target_raise) : "—"}</div><div className="text-[9px] text-[#999]">Target</div></div>
                <div className="text-center p-2 rounded-lg bg-[#faf7f2]"><div className="text-sm font-bold text-[#222]">{c.property_address || c.state || "—"}</div><div className="text-[9px] text-[#999]">Location</div></div>
              </div>
              {c.lead_id && <div className="mt-2 flex gap-3"><EntityLink type="commitment" id={c.commitment_id} label="View Commitment" onClick={navigateToEntity} className="text-[10px]" /><EntityLink type="deal" id={c.lead_id} label="View Deal" onClick={navigateToEntity} className="text-[10px]" /></div>}
            </div>
          ))}
        </div>
      )}

      {tab === "distributions" && (
        <div className="space-y-2">
          {distributions.length === 0 ? <div className="rounded-xl border border-[#e8e0d0] bg-white p-8 text-center text-sm text-[#888]">No distributions yet.</div> : distributions.map((d, i) => (
            <div key={`dist-${i}`} className="rounded-xl border border-[#e8e0d0] bg-white p-3 flex items-center justify-between">
              <div><span className="text-xs font-bold text-emerald-600">{fmtUSD(d.amount)}</span><span className="text-[10px] text-[#888] ml-2">{d.dist_type || "Distribution"}</span></div>
              <span className="text-[10px] text-[#bbb]">{d.created_at ? new Date(d.created_at).toLocaleDateString() : ""}</span>
            </div>
          ))}
        </div>
      )}

      {tab === "servicing" && <InvestorServicingPanel servicing={servicing} />}

      {tab === "pools" && (
        <div className="space-y-2">
          {pool_subscriptions.length === 0 ? <div className="rounded-xl border border-[#e8e0d0] bg-white p-8 text-center text-sm text-[#888]">No pool subscriptions.</div> : pool_subscriptions.map((s, i) => (
            <div key={`pool-${i}`} className="rounded-xl border border-[#e8e0d0] bg-white p-3"><div className="flex items-center justify-between"><span className="text-xs font-medium text-[#333]">Pool {s.pool_id?.slice(0, 8)}</span><span className="text-xs font-bold text-emerald-600">{fmtUSD(s.allocation_amount)}</span></div></div>
          ))}
        </div>
      )}

      {tab === "activity" && (
        <div className="space-y-2">
          {alerts.length === 0 ? <div className="rounded-xl border border-[#e8e0d0] bg-white p-8 text-center text-sm text-[#888]">No activity.</div> : alerts.map((a, i) => (
            <div key={`alert-${i}`} className="rounded-xl border border-[#e8e0d0] bg-white p-3 flex items-start gap-2"><Bell className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" /><div><p className="text-xs text-[#333]">{a.message}</p><p className="text-[10px] text-[#bbb] mt-0.5">{a.created_at ? new Date(a.created_at).toLocaleString() : ""}</p></div></div>
          ))}
        </div>
      )}

      {tab === "documents" && <ProfileDocuments userId={userId} documents={documents} isAdmin={isInternal} onRefresh={() => { axios.get(`${API}/entity/investor/${userId}/360`, { headers }).then(r => setData(r.data)).catch(console.error); }} />}

      {tab === "preferences" && preferences && (
        <div className="rounded-xl border border-[#e8e0d0] bg-white p-4">
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#999] mb-3">Investment Preferences</h3>
          <div className="grid grid-cols-2 gap-3 text-xs">
            {preferences.preferred_loan_types?.length > 0 && <div><span className="text-[#888]">Loan Types:</span><div className="flex flex-wrap gap-1 mt-1">{preferences.preferred_loan_types.map(lt => <span key={lt} className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 text-[10px]">{lt}</span>)}</div></div>}
            {preferences.preferred_states?.length > 0 && <div><span className="text-[#888]">States:</span><div className="flex flex-wrap gap-1 mt-1">{preferences.preferred_states.map(st => <span key={st} className="px-1.5 py-0.5 rounded bg-[#faf7f2] text-[#888] text-[10px]">{st}</span>)}</div></div>}
            {preferences.min_yield && <div><span className="text-[#888]">Min Yield:</span> <span className="font-semibold">{preferences.min_yield}%</span></div>}
            {preferences.max_ltv && <div><span className="text-[#888]">Max LTV:</span> <span className="font-semibold">{preferences.max_ltv}%</span></div>}
          </div>
        </div>
      )}

      {tab === "reputation" && <ReputationDetailCard data={reputation} entityType="Investor" />}
      {tab === "comms" && <EntityComms entityType="investor" entityId={userId} />}
      {tab === "notes" && <EntityNotes entityType="investor" entityId={userId} />}
    </Entity360Shell>
  );
}
